import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type { TargetProbeLegRecord } from '@nadoutong/contracts';
import { Ban, Play, Plus, RotateCcw } from 'lucide-react';
import { TARGET_PROBES_KEY, useProbeTargetNow, useResetTargetProbe } from '../../api/useTargetProbes.ts';
import {
  useSetRoutingConfigSkuTarget,
  useSetRoutingDefaultSkuTarget,
} from '../../api/useTenantRouting.ts';
import { coerceErrorMessage, useToast } from '../../components/Toast.tsx';
import { useSession } from '../../hooks/useSession.tsx';
import { useT } from '../../i18n/index.ts';
import { Chip, ConfirmDialog, IconButton, Select, StatusLamp, Td, Tooltip, Tr } from '../../ui/index.ts';
import { DisabledNoteField } from '../tenant-routing/DisabledNoteField.tsx';
import { TryProbeButton } from '../tenant-routing/TryProbeButton.tsx';
import { disabledNoteForWire } from '../tenant-routing/disabledNote.ts';
import { disabledMemberNotes, legEvidence } from './legEvidence.ts';
import { LegLocationsLink } from './LegLocationsLink.tsx';
import {
  legHasLiveDeprecatedConfig,
  legMemberBreakdown,
  legReachable,
  legWeightSummary,
} from './legLocations.ts';
import {
  immunityMinutes,
  immunityMinutesRemaining,
  isImmunityChoice,
  legAnchorMember,
  legLamp,
  legLampTone,
  legMaxWeight,
  minutesBefore,
  minutesUntil,
  LEG_UNIQUE,
  type ImmunityChoice,
  type LegLamp,
  type LegLookalike,
  type LegSkuArming,
} from './legModel.ts';
import { locateProbeMember, routingTargetWriteBody } from './routingTargetWrite.ts';
import { shortIdentifier } from '../tenantRoutingShared.ts';

function memberId(leg: TargetProbeLegRecord): string {
  return leg.members[0]?.target_id ?? `${leg.sku_id}:${leg.upstream_id}:${leg.upstream_channel_id}`;
}

/**
 * 「全局默认 权重 0（不首发） · 另在 2 个客户配置中首发」: the weight where it
 * serves, not the max across every member. Older gateways (no reach fields)
 * keep the max; no known weight on a reachable member prints nothing.
 */
function legWeightText(leg: TargetProbeLegRecord, t: ReturnType<typeof useT>): readonly string[] | null {
  const summary = legWeightSummary(leg);
  if (summary === null) return null;
  if (summary.kind === 'legacy') {
    const maxWeight = legMaxWeight(leg);
    if (maxWeight === null) return null;
    return [maxWeight > 0
      ? t('guaranteed.leg.weight', { weight: String(maxWeight) })
      : t('guaranteed.leg.weight_zero')];
  }
  const parts: string[] = [];
  const { primary } = summary;
  if (primary !== null) {
    parts.push(primary.weight > 0
      ? t(`guaranteed.leg.weight_${primary.source}`, { weight: String(primary.weight) })
      : t(`guaranteed.leg.weight_${primary.source}_zero`));
  }
  if (summary.orgFirstPick > 0) {
    const count = String(summary.orgFirstPick);
    parts.push(primary === null
      ? t('guaranteed.leg.weight_org_only', { count })
      : t('guaranteed.leg.weight_org_also', { count }));
  }
  if (parts.length === 0) return summary.allZero ? [t('guaranteed.leg.weight_zero')] : null;
  return parts;
}

/** 「61 个目标：不接流量 61（全部在已过时配置）」 — where the shared targets sit. */
function memberWhere(leg: TargetProbeLegRecord, t: ReturnType<typeof useT>): string {
  const breakdown = legMemberBreakdown(leg);
  const parts: string[] = [];
  for (const reason of ['global', 'override', 'org_assigned', 'default_fallthrough'] as const) {
    const count = breakdown.byReason.get(reason) ?? 0;
    if (count > 0) parts.push(`${t(`guaranteed.leg.reach.${reason}`)} ${count}`);
  }
  if (breakdown.unreachable > 0) {
    const count = String(breakdown.unreachable);
    parts.push(breakdown.unreachableDeprecated === 0
      ? t('guaranteed.leg.members_where_unreachable', { count })
      : breakdown.unreachableDeprecated === breakdown.unreachable
        ? t('guaranteed.leg.members_where_unreachable_dep_all', { count })
        : t('guaranteed.leg.members_where_unreachable_dep', {
          count,
          deprecated: String(breakdown.unreachableDeprecated),
        }));
  }
  const unknown = breakdown.byReason.get('unknown') ?? 0;
  if (unknown > 0) parts.push(`${t('guaranteed.leg.reach.unknown')} ${unknown}`);
  return t('guaranteed.leg.members_where', { total: String(breakdown.total), parts: parts.join(' · ') });
}

function chipLabel(lamp: LegLamp, immuneLeft: number | null, armed: boolean, t: ReturnType<typeof useT>): string {
  switch (lamp) {
    case 'ok':
      return t('guaranteed.leg.chip.ok');
    case 'shadow':
      return t('guaranteed.leg.chip.shadow');
    case 'failing':
      return t('guaranteed.leg.chip.failing');
    case 'tripped':
      return armed ? t('guaranteed.leg.chip.tripped') : t('guaranteed.leg.chip.tripped_unarmed');
    case 'manual':
      return t('guaranteed.leg.chip.manual');
    case 'immune':
      return t('guaranteed.leg.chip.immune', { minutes: String(immuneLeft ?? 0) });
    case 'unprobed':
      return t('guaranteed.leg.unprobed');
  }
}

function chipTone(lamp: LegLamp, armed: boolean): 'ok' | 'warn' | 'bad' | 'signal' | 'neutral' {
  switch (lamp) {
    case 'ok':
      return 'ok';
    case 'shadow':
    case 'failing':
      return 'warn';
    case 'tripped':
      return armed ? 'bad' : 'neutral';
    case 'immune':
      return 'signal';
    case 'manual':
    case 'unprobed':
      return 'neutral';
  }
}

export function LegRow({ leg, nowMs, arming, lookalike = LEG_UNIQUE }: {
  readonly leg: TargetProbeLegRecord;
  readonly nowMs: number;
  readonly arming: LegSkuArming;
  /** Another leg of this SKU reads the same (upstream · model · channel): what to add to tell them apart. */
  readonly lookalike?: LegLookalike;
}): JSX.Element {
  const t = useT();
  const toast = useToast();
  const { fetchImpl } = useSession();
  const reset = useResetTargetProbe();
  const probeNow = useProbeTargetNow();
  const setDefault = useSetRoutingDefaultSkuTarget();
  const setConfig = useSetRoutingConfigSkuTarget();
  const qc = useQueryClient();
  const [resetOpen, setResetOpen] = useState(false);
  const [immunity, setImmunity] = useState<ImmunityChoice>('5');
  const [disableOpen, setDisableOpen] = useState(false);
  const [disableNote, setDisableNote] = useState('');
  const [enableOpen, setEnableOpen] = useState(false);
  const [writing, setWriting] = useState(false);
  const lamp = legLamp(leg, nowMs);
  const targetId = memberId(leg);
  // Reset targets an enabled member first. Probe-now / 试打 also reach a
  // manually disabled one (the gateway only records the shot for it).
  const anchor = legAnchorMember(leg);
  const armed = arming.armed;
  const immuneLeft = immunityMinutesRemaining(leg.immune_until, nowMs);
  const rawEvidence = legEvidence(leg, lamp, nowMs, t, arming);
  const unarmedTrip = lamp === 'tripped' && !armed;
  const evidence = unarmedTrip ? `${rawEvidence} · ${t('guaranteed.leg.verdict.unarmed_short')}` : rawEvidence;
  const evidenceTip = unarmedTrip ? `${rawEvidence}\n${t('guaranteed.leg.verdict.unarmed')}` : rawEvidence;
  const memberTip = `${memberWhere(leg, t)}\n${t('guaranteed.leg.members_tip', { count: String(leg.members.length) })}`;
  const reachable = legReachable(leg);
  const agoMin = leg.last_probe_at === null ? null : minutesBefore(leg.last_probe_at, nowMs);
  const nextMin = minutesUntil(leg.next_probe_at, nowMs);
  const nextText = !arming.probesOn
    ? t('guaranteed.leg.next_probes_off')
    : nextMin === null
      ? t('guaranteed.leg.never')
      : nextMin === 0
        ? t('guaranteed.leg.next_due')
        : t('guaranteed.leg.minutes_in', { minutes: String(nextMin) });
  const busy = reset.isPending || probeNow.isPending || writing;
  const sub = `${leg.physical_model} · ${leg.upstream_channel_id}`;
  // Own line: the sub line truncates in this column and would cut exactly the
  // part that tells look-alike legs apart.
  // Region leads: it is short and, for JP / CN slices of one supply, the only difference.
  const supplyTail = [
    ...(lookalike.region ? [leg.region.toUpperCase()] : []),
    ...(lookalike.tails ? [shortIdentifier(leg.account_id), shortIdentifier(leg.key_bucket_id)] : []),
  ].join(' · ');
  const weightText = legWeightText(leg, t);

  async function writeEnabled(enabled: boolean): Promise<void> {
    setWriting(true);
    const note = enabled ? undefined : disabledNoteForWire(disableNote);
    try {
      for (const member of leg.members) {
        const found = await locateProbeMember(member, leg.sku_id, fetchImpl);
        const body = found === null ? null : routingTargetWriteBody(found.target, enabled, note);
        if (found === null || body === null) {
          toast.error(t('tenant_routing.probe.disable_missing'));
          return;
        }
        if (found.layer === 'default') {
          await setDefault.mutateAsync({ skuId: leg.sku_id, body });
        } else {
          await setConfig.mutateAsync({ configId: found.configId, skuId: leg.sku_id, body });
        }
      }
      // The legs read carries enabled + note; refresh it so the lamp flips now.
      void qc.invalidateQueries({ queryKey: TARGET_PROBES_KEY });
      toast.success(t(enabled ? 'guaranteed.leg.enable_done' : 'tenant_routing.probe.disable_done'));
      setDisableOpen(false);
      setEnableOpen(false);
    } catch (error) {
      toast.error(coerceErrorMessage(error));
    } finally {
      setWriting(false);
    }
  }

  const resetLabel = lamp === 'shadow' ? t('guaranteed.leg.reset_shadow') : t('guaranteed.leg.reset');

  return (
    <Tr data-testid={`guaranteed-leg-${targetId}`} data-lamp={lamp}>
      <Td className="py-1.5 whitespace-nowrap">
        <span className="inline-flex items-center gap-1.5 whitespace-nowrap">
          <StatusLamp tone={legLampTone(lamp)} testId={`guaranteed-leg-lamp-${targetId}`} />
          <Chip tone={chipTone(lamp, armed)} lamp={false}>{chipLabel(lamp, immuneLeft, armed, t)}</Chip>
        </span>
      </Td>
      <Td className="py-1.5">
        <span className="block truncate font-medium text-paper" title={leg.upstream_name}>
          {leg.upstream_name}
          {lamp !== 'manual' && leg.members.some((member) => !member.enabled) ? (
            <span
              className="ml-1.5 font-normal text-micro text-paper-low"
              title={[t('guaranteed.leg.partly_disabled_tip'), disabledMemberNotes(leg, t)]
                .filter((part) => part.length > 0).join('\n')}
              data-testid={`guaranteed-leg-partly-disabled-${targetId}`}
            >
              {t('guaranteed.leg.partly_disabled', {
                disabled: String(leg.members.filter((member) => !member.enabled).length),
                total: String(leg.members.length),
              })}
            </span>
          ) : null}
        </span>
        {/* Own line: 「全局默认 权重 0（不首发） · 另在 2 个客户配置中首发」 would be cut on the name line. */}
        {weightText === null ? null : (
          <span
            className="flex flex-wrap gap-x-1.5 font-mono text-micro text-paper-low"
            title={weightText.join(' · ')}
            data-testid={`guaranteed-leg-weight-${targetId}`}
          >
            {weightText.map((part, index) => (
              <span key={part} className="whitespace-nowrap">{index > 0 ? `· ${part}` : part}</span>
            ))}
          </span>
        )}
        <span
          className="block truncate font-mono text-micro text-paper-low"
          title={sub}
          data-testid={`guaranteed-leg-sub-${targetId}`}
        >
          {!reachable ? (
            <span className="mr-1.5 inline-flex align-middle font-sans" title={t('guaranteed.leg.tag.unreachable_tip')}>
              <Chip tone="neutral" lamp={false} testId={`guaranteed-leg-unreachable-${targetId}`}>
                {t('guaranteed.leg.tag.unreachable')}
              </Chip>
            </span>
          ) : legHasLiveDeprecatedConfig(leg) ? (
            <span className="mr-1.5 inline-flex align-middle font-sans" title={t('guaranteed.leg.tag.live_deprecated_tip')}>
              <Chip tone="neutral" lamp={false} testId={`guaranteed-leg-live-deprecated-${targetId}`}>
                {t('guaranteed.leg.tag.live_deprecated')}
              </Chip>
            </span>
          ) : null}
          {sub}
        </span>
        {supplyTail.length === 0 ? null : (
          <span
            className="block truncate font-mono text-micro text-paper-low"
            title={supplyTail}
            data-testid={`guaranteed-leg-supply-${targetId}`}
          >
            {supplyTail}
          </span>
        )}
      </Td>
      <Td className="py-1.5">
        <span className="block truncate" title={evidenceTip} data-testid={`guaranteed-leg-evidence-${targetId}`}>
          {evidence}
        </span>
      </Td>
      <Td className="py-1.5 whitespace-nowrap">
        {agoMin === null ? t('guaranteed.leg.never') : t('guaranteed.leg.minutes_ago', { minutes: String(agoMin) })}
        {leg.last_status_code === null ? null : (
          <span className="ml-1 font-mono">HTTP {leg.last_status_code}</span>
        )}
      </Td>
      <Td className="py-1.5 whitespace-nowrap text-paper-mid">{nextText}</Td>
      <Td num className="py-1.5">{leg.backoff_level}</Td>
      <Td num className="py-1.5">
        <span className="inline-flex items-center justify-end gap-0.5">
          <Tooltip content={<span className="whitespace-pre-line">{memberTip}</span>}>
            <span className="cursor-default underline decoration-line" title={memberTip} data-testid={`guaranteed-leg-members-${targetId}`}>
              {leg.members.length}
            </span>
          </Tooltip>
          <LegLocationsLink leg={leg} testKey={targetId} />
        </span>
      </Td>
      <Td className="py-1.5">
        <span className="inline-flex items-center gap-0.5">
          {lamp === 'manual' ? null : (
            <Tooltip content={resetLabel}>
              <IconButton
                label={resetLabel}
                size="sm"
                disabled={busy || anchor === undefined}
                data-testid={`tenant-routing-target-probe-reset-${targetId}`}
                onClick={() => {
                  setImmunity('5');
                  setResetOpen(true);
                }}
              >
                <RotateCcw aria-hidden="true" className="h-3.5 w-3.5" />
              </IconButton>
            </Tooltip>
          )}
          <Tooltip content={t('guaranteed.leg.probe_now_tip')}>
            <IconButton
              label={t('guaranteed.leg.probe_now')}
              size="sm"
              disabled={busy || anchor === undefined}
              data-testid={`tenant-routing-target-probe-now-${targetId}`}
              onClick={() => {
                if (anchor === undefined) return;
                probeNow.mutate(
                  { layer: anchor.layer, target_id: anchor.target_id },
                  {
                    onSuccess: (result) => {
                      const code = result.state.last_status_code;
                      toast.success(code === null
                        ? t('guaranteed.leg.probe_now_done_nocode')
                        : t('guaranteed.leg.probe_now_done', { code: String(code) }));
                    },
                    onError: (error) => toast.error(coerceErrorMessage(error)),
                  },
                );
              }}
            >
              <Play aria-hidden="true" className="h-3.5 w-3.5" />
            </IconButton>
          </Tooltip>
          {anchor === undefined ? null : (
            <TryProbeButton layer={anchor.layer} targetId={anchor.target_id} disabled={busy} />
          )}
          {lamp === 'manual' ? (
            <Tooltip content={t('guaranteed.leg.enable')}>
              <IconButton
                label={t('guaranteed.leg.enable')}
                size="sm"
                disabled={busy}
                data-testid={`guaranteed-leg-enable-${targetId}`}
                onClick={() => setEnableOpen(true)}
              >
                <Plus aria-hidden="true" className="h-3.5 w-3.5" />
              </IconButton>
            </Tooltip>
          ) : (
            <Tooltip content={t('guaranteed.leg.disable')}>
              <IconButton
                label={t('guaranteed.leg.disable')}
                size="sm"
                disabled={busy}
                className="hover:text-bad"
                data-testid={`tenant-routing-target-probe-disable-${targetId}`}
                onClick={() => {
                  setDisableNote('');
                  setDisableOpen(true);
                }}
              >
                <Ban aria-hidden="true" className="h-3.5 w-3.5" />
              </IconButton>
            </Tooltip>
          )}
        </span>
      </Td>
      <ConfirmDialog
        open={resetOpen}
        onOpenChange={setResetOpen}
        title={t('guaranteed.leg.reset_title')}
        confirmLabel={t('guaranteed.leg.reset')}
        cancelLabel={t('common.cancel')}
        closeLabel={t('common.close')}
        testId={`guaranteed-leg-reset-dialog-${targetId}`}
        loading={reset.isPending}
        body={(
          <div className="flex flex-col gap-2">
            <p className="m-0">{t('guaranteed.leg.reset_body')}</p>
            <span className="text-label text-paper-low">{t('guaranteed.leg.immunity')}</span>
            <Select
              value={immunity}
              onValueChange={(value) => {
                if (isImmunityChoice(value)) setImmunity(value);
              }}
              options={[
                { value: '0', label: t('guaranteed.leg.immunity.0') },
                { value: '5', label: t('guaranteed.leg.immunity.5') },
                { value: '10', label: t('guaranteed.leg.immunity.10') },
                { value: '30', label: t('guaranteed.leg.immunity.30') },
                { value: '60', label: t('guaranteed.leg.immunity.60') },
              ]}
              ariaLabel={t('guaranteed.leg.immunity')}
              testId={`guaranteed-leg-immunity-${targetId}`}
            />
          </div>
        )}
        onConfirm={() => {
          if (anchor === undefined) return;
          reset.mutate(
            {
              layer: anchor.layer,
              target_id: anchor.target_id,
              immunity_minutes: immunityMinutes(immunity),
            },
            {
              onSuccess: () => {
                toast.success(t('tenant_routing.probe.reset_done'));
                setResetOpen(false);
              },
              onError: (error) => toast.error(coerceErrorMessage(error)),
            },
          );
        }}
      />
      <ConfirmDialog
        open={disableOpen}
        onOpenChange={setDisableOpen}
        tone="danger"
        title={t('tenant_routing.probe.disable_title')}
        body={(
          <div className="flex flex-col gap-3">
            <p className="m-0">{t('tenant_routing.probe.disable_body')}</p>
            <DisabledNoteField
              value={disableNote}
              onChange={setDisableNote}
              fieldId={`guaranteed-leg-disable-note-${targetId}`}
              testId={`guaranteed-leg-disable-note-${targetId}`}
            />
          </div>
        )}
        confirmLabel={t('tenant_routing.probe.disable_confirm')}
        cancelLabel={t('common.cancel')}
        closeLabel={t('common.close')}
        loading={writing}
        testId={`tenant-routing-target-probe-disable-dialog-${targetId}`}
        onConfirm={() => { void writeEnabled(false); }}
      />
      <ConfirmDialog
        open={enableOpen}
        onOpenChange={setEnableOpen}
        title={t('guaranteed.leg.enable_title')}
        body={t('guaranteed.leg.enable_body')}
        confirmLabel={t('guaranteed.leg.enable')}
        cancelLabel={t('common.cancel')}
        closeLabel={t('common.close')}
        loading={writing}
        testId={`guaranteed-leg-enable-dialog-${targetId}`}
        onConfirm={() => { void writeEnabled(true); }}
      />
    </Tr>
  );
}
